import React, { Fragment, useCallback, useContext, useState } from 'react';
import {
  SalvBanner,
  SalvBannerType,
  SalvBannerAccent,
  createColumnsForObjectData,
  SalvCheckbox,
  SalvDataTag,
  SalvTable,
} from '@salvapp/ui-components';
import { ScreenableFieldPart, useUsableFieldsBuilder } from '../helpers/useUsableFieldsBuilder';
import { ExpandedState } from '@tanstack/react-table';
import { useTableNestedHack } from '../helpers/useTableNestedHack';
import { clsx } from 'clsx';
import { ScreenableFieldSetupContext } from '../helpers/useScreenableFieldSetupContextManager';
import { ObjectSchemaFieldDataTag } from '../../configure/new/helper';

export type UsableField = {
  id: string;
  fieldName: string;
};

export const ScreenableDataStep = () => {
  const { entityType, usableFields, updateScreenableField } = useContext(ScreenableFieldSetupContext);
  const setSelectedFields = useCallback(
    (fields: ScreenableFieldPart[]) => {
      updateScreenableField(fields.map((f) => ({ id: f.id, fieldName: f.fullFieldName }) as UsableField));
    },
    [updateScreenableField]
  );

  const [allFields, selectedFields, selectableFieldIds, errors] = useUsableFieldsBuilder(
    usableFields.map((it) => it.id),
    entityType
  );
  const [fieldsAvailableInTable, fieldsActiveInTable, setFieldsFromTable, toggleField] = useTableNestedHack(
    allFields,
    selectedFields,
    setSelectedFields
  );

  const [expanded, setExpanded] = useState<ExpandedState>();

  return (
    <div className='screenable-data-step'>
      <div className='screenable-data-step-schema'>
        <h4 className='text-strong-m chapter-title'>Fields used for screening</h4>
        <span className='text-s chapter-subtitle'>Select your data that will be used for screening.</span>
        {errors.length > 0 && (
          <SalvBanner
            type={SalvBannerType.DANGER}
            accent={SalvBannerAccent.LIGHT}
            className='m-b-8'
            message={errors.join('\n')}
          />
        )}
        <SalvTable
          allowSelectAll={false}
          columns={schemaTableColumns}
          data={fieldsAvailableInTable}
          expanded={expanded}
          setExpanded={setExpanded}
          selectedRows={fieldsActiveInTable}
          setSelectedRows={setFieldsFromTable}
          isRowSelectionEnabled={(row) => selectableFieldIds.has(row.original.id)}
          getExpandDescription={(row) => row.original.children.length + ' fields available'}
        >
          {(row) => {
            if (row.original.children.length <= 0) {
              return null;
            }
            return row.original.children.map((child) => {
              return (
                <FieldSubRow
                  key={child.id}
                  row={child}
                  toggleRow={toggleField}
                  selectedRowIds={selectedFields.map((f) => f.id)}
                  selectableFieldIds={selectableFieldIds}
                />
              );
            });
          }}
        </SalvTable>
      </div>
      <div className='screenable-data-step-preview'>
        <h4 className='text-strong-m chapter-title'>Field relation</h4>
        <span className='text-s chapter-subtitle'>
          If multiple screened fields are selected, review how the fields are ordered. Fields are added in sequential
          order based on the field selection. To adjust, re-select fields in the desired order on the left.
        </span>
        {usableFields.length > 0 && <span className='caps-xs chapter-minititle'>Field name</span>}
        <div className={clsx('screenable-data-field', usableFields.length > 1 && 'grouped')}>
          {usableFields.map((row, index, array) => (
            <Fragment key={row.id}>
              {row.fieldName.includes('.') ? (
                <ObjectSchemaFieldDataTag key={row.id} value={row.fieldName} />
              ) : (
                <SalvDataTag key={row.id} value={row.fieldName} />
              )}
              {index < array.length - 1 && <span>+</span>}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const FieldSubRow = (props: {
  row: ScreenableFieldPart;
  selectedRowIds: string[];
  selectableFieldIds: Set<string>;
  toggleRow: (row: ScreenableFieldPart) => void;
}) => {
  return (
    <div className='screening-field-sub-row'>
      <SalvCheckbox
        className='checkbox'
        selected={props.selectedRowIds.includes(props.row.id)}
        onChange={() => props.toggleRow(props.row)}
        disabled={!props.selectableFieldIds.has(props.row.id)}
        ariaLabel={props.row.displayName}
      />
      <span>{props.row.displayName}</span>
    </div>
  );
};

const schemaTableColumns = createColumnsForObjectData<ScreenableFieldPart>([
  {
    accessorKey: 'displayName',
    header: 'Field name',
  },
]);
